import {
  ELiveDataMetrics,
  ELiveDataMetricsKey,
  ELiveDataMetricsString,
  ELiveDataTargetedRateMetrics,
} from '~/src/dash/api/live_data/models';
import {
  EMonitorAlertTimeframe,
  EMonitorLookbackType,
  EMonitorMetricDirection,
  EMonitorMetricsOnlyValueAlertThreshold,
  EMonitorMetricsWithNoTimeframe,
  EMonitorNotificationProvider,
  EMonitorObjectType,
  EMonitorObjectTypesWithoutCustomMetricType,
  EMonitorThresholdComputationType,
  EMonitorTriggerType,
  EMonitorType,
  IMonitor,
  IMonitorCustomMetric,
} from '~/src/dash/api/monitors/monitors_models';
import { ISimpleOptionSelect } from '~/src/dash/publicaUI';
import { extractRouteParams, getRoutePath, RouteMonitorEdit } from '~/src/dash/router/routes';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';

export function GetMonitorEditRoute(monitor: IMonitor, props: any): string {
  return getRoutePath(RouteMonitorEdit, extractRouteParams(props), {
    monitorid: monitor.ID,
  });
}

export function MonitorObjectTypeToString(m: EMonitorObjectType): string {
  switch (m) {
    case EMonitorObjectType.MonitorObjectType_UNKNOWN:
      return 'Unknown';
    case EMonitorObjectType.MonitorObjectType_BIDDER:
      return 'Bidder';
    case EMonitorObjectType.MonitorObjectType_SITE_BIDDER:
      return 'Bidder For Channel';
    case EMonitorObjectType.MonitorObjectType_SITE:
      return 'Channel';
    case EMonitorObjectType.MonitorObjectType_DEMAND_SOURCE:
      return 'Demand Source';
    case EMonitorObjectType.MonitorObjectType_BIDDER_GROUP:
      return 'Bidder Group';
    case EMonitorObjectType.MonitorObjectType_PUBLISHER:
      return 'Publisher';
    case EMonitorObjectType.MonitorObjectType_CAMPAIGN:
      return 'Campaign';
    case EMonitorObjectType.MonitorObjectType_CHANNEL_GROUP:
      return 'Channel Group';
    case EMonitorObjectType.MonitorObjectType_NETWORK:
      return 'Network';
    case EMonitorObjectType.MonitorObjectType_CHECKPOINT:
      return 'Campaign Checkpoint';
  }
  return 'Unknown';
}

// if EnableTargetAllActive && Multi Monitor Type => use individual name (bidder, channel) rather than group
// if EnableTargetAllActive && Single Monitor Type => use group name 'all active' + individual name (bidder, channel) = 's'
// if !EnableTargetAllActive => use normal name
export function MonitorObjectTypeToStringV2(
  m: EMonitorObjectType,
  EnableTargetAllActive: boolean,
  MonitorType: number,
): string {
  let objectType = 'Unknown';
  switch (m) {
    case EMonitorObjectType.MonitorObjectType_UNKNOWN:
      objectType = 'Unknown';
      break;
    case EMonitorObjectType.MonitorObjectType_BIDDER:
      objectType = 'Bidder';
      break;
    case EMonitorObjectType.MonitorObjectType_SITE_BIDDER:
      objectType = 'Bidder For Channel';
      break;
    case EMonitorObjectType.MonitorObjectType_SITE:
      objectType = 'Channel';
      break;
    case EMonitorObjectType.MonitorObjectType_DEMAND_SOURCE:
      objectType = 'Demand Source';
      break;
    case EMonitorObjectType.MonitorObjectType_BIDDER_GROUP:
      objectType = 'Bidder Group';
      if (MonitorType === EMonitorType.MonitorType_MULTI) {
        objectType = 'Bidder';
      }
      break;
    case EMonitorObjectType.MonitorObjectType_PUBLISHER:
      objectType = 'Publisher';
      break;
    case EMonitorObjectType.MonitorObjectType_CAMPAIGN:
      objectType = 'Campaign';
      break;
    case EMonitorObjectType.MonitorObjectType_CHANNEL_GROUP:
      objectType = 'Channel Group';
      if (MonitorType === EMonitorType.MonitorType_MULTI) {
        objectType = 'Channel';
      }
      break;
    case EMonitorObjectType.MonitorObjectType_NETWORK:
      objectType = 'Network';
      break;
  }

  if (EnableTargetAllActive && MonitorType === EMonitorType.MonitorType_SINGLE) {
    objectType = 'All Active ' + objectType + 's';
  }

  return objectType;
}

// utils for view
export function genObjectTypeList(selectedObjectType: EMonitorObjectType): IMenuList {
  const out = {} as IMenuList;

  out[EMonitorObjectType.MonitorObjectType_PUBLISHER] = {
    title: 'Publisher',
    ID: EMonitorObjectType.MonitorObjectType_PUBLISHER.toString(),
    selected: selectedObjectType === EMonitorObjectType.MonitorObjectType_PUBLISHER,
  };

  out[EMonitorObjectType.MonitorObjectType_SITE] = {
    title: 'Channel',
    ID: EMonitorObjectType.MonitorObjectType_SITE.toString(),
    selected: selectedObjectType === EMonitorObjectType.MonitorObjectType_SITE,
  };

  out[EMonitorObjectType.MonitorObjectType_CHANNEL_GROUP] = {
    title: 'Channel Group',
    ID: EMonitorObjectType.MonitorObjectType_CHANNEL_GROUP.toString(),
    selected: selectedObjectType === EMonitorObjectType.MonitorObjectType_CHANNEL_GROUP,
  };

  out[EMonitorObjectType.MonitorObjectType_BIDDER] = {
    title: 'Bidder',
    ID: EMonitorObjectType.MonitorObjectType_BIDDER.toString(),
    selected: selectedObjectType === EMonitorObjectType.MonitorObjectType_BIDDER,
  };

  out[EMonitorObjectType.MonitorObjectType_DEMAND_SOURCE] = {
    title: 'Demand Source',
    ID: EMonitorObjectType.MonitorObjectType_DEMAND_SOURCE.toString(),
    selected: selectedObjectType === EMonitorObjectType.MonitorObjectType_DEMAND_SOURCE,
  };

  out[EMonitorObjectType.MonitorObjectType_BIDDER_GROUP] = {
    title: 'Bidder Group',
    ID: EMonitorObjectType.MonitorObjectType_BIDDER_GROUP.toString(),
    selected: selectedObjectType === EMonitorObjectType.MonitorObjectType_BIDDER_GROUP,
  };

  out[EMonitorObjectType.MonitorObjectType_CAMPAIGN] = {
    title: 'Campaign',
    ID: EMonitorObjectType.MonitorObjectType_CAMPAIGN.toString(),
    selected: selectedObjectType === EMonitorObjectType.MonitorObjectType_CAMPAIGN,
  };

  out[EMonitorObjectType.MonitorObjectType_NETWORK] = {
    title: 'Network',
    ID: EMonitorObjectType.MonitorObjectType_NETWORK.toString(),
    selected: selectedObjectType === EMonitorObjectType.MonitorObjectType_NETWORK,
  };

  out[EMonitorObjectType.MonitorObjectType_CHECKPOINT] = {
    title: 'Campaign Checkpoint',
    ID: EMonitorObjectType.MonitorObjectType_CHECKPOINT.toString(),
    selected: selectedObjectType === EMonitorObjectType.MonitorObjectType_CHECKPOINT,
  };

  return out;
}

export function genMetricDirectionList(selectedDirection: EMonitorMetricDirection): IMenuList {
  const out = {} as IMenuList;

  out[EMonitorMetricDirection.MonitorMetricDirection_ABOVE] = {
    title: 'Rises Above',
    ID: EMonitorMetricDirection.MonitorMetricDirection_ABOVE.toString(),
    selected: false,
  };
  out[EMonitorMetricDirection.MonitorMetricDirection_UNDER] = {
    title: 'Drops Below',
    ID: EMonitorMetricDirection.MonitorMetricDirection_UNDER.toString(),
    selected: false,
  };

  out[selectedDirection].selected = true;
  return out;
}

export function genThresholdComputationTypeList(
  selected: EMonitorThresholdComputationType,
  isRateMetric: boolean,
  isLatencyMetric: boolean,
): IMenuList {
  const out = {} as IMenuList;

  if (isRateMetric) {
    out[EMonitorThresholdComputationType.MonitorThresholdComputationType_PERCENTAGE] = {
      title: 'Percentage',
      ID: EMonitorThresholdComputationType.MonitorThresholdComputationType_PERCENTAGE.toString(),
      selected: false,
    };
  } else if (isLatencyMetric) {
    out[EMonitorThresholdComputationType.MonitorThresholdComputationType_AVG] = {
      title: 'Average',
      ID: EMonitorThresholdComputationType.MonitorThresholdComputationType_AVG.toString(),
      selected: false,
    };
  } else {
    out[EMonitorThresholdComputationType.MonitorThresholdComputationType_SUM] = {
      title: 'Total',
      ID: EMonitorThresholdComputationType.MonitorThresholdComputationType_SUM.toString(),
      selected: false,
    };
    out[EMonitorThresholdComputationType.MonitorThresholdComputationType_AVG] = {
      title: 'Average',
      ID: EMonitorThresholdComputationType.MonitorThresholdComputationType_AVG.toString(),
      selected: false,
    };
  }
  if (out[selected]) {
    out[selected].selected = true;
  }

  return out;
}

export function isMetricOnlyValueAlertThreshold(metric: ELiveDataMetrics) {
  return EMonitorMetricsOnlyValueAlertThreshold.has(metric);
}

export function isMetricWithoutTimeframe(metric: ELiveDataMetrics) {
  return EMonitorMetricsWithNoTimeframe.has(metric);
}

export function isMonitorObjectTypeWithoutCustomMetricType(objectType: EMonitorObjectType) {
  return EMonitorObjectTypesWithoutCustomMetricType.has(objectType);
}

export function genTimeframeList(val: EMonitorAlertTimeframe, noTimeframe?: boolean): IListItem[] {
  const out = [] as IListItem[];

  if (noTimeframe) {
    return out;
  }

  out.push({
    title: '15min',
    ID: EMonitorAlertTimeframe.MonitorAlertTimeframe_15MIN.toString(),
    selected: false,
  });
  out.push({
    title: '30min',
    ID: EMonitorAlertTimeframe.MonitorAlertTimeframe_30MIN.toString(),
    selected: false,
  });
  out.push({
    title: '1h',
    ID: EMonitorAlertTimeframe.MonitorAlertTimeframe_1H.toString(),
    selected: false,
  });
  out.push({
    title: '2h',
    ID: EMonitorAlertTimeframe.MonitorAlertTimeframe_2H.toString(),
    selected: false,
  });
  out.push({
    title: '4h',
    ID: EMonitorAlertTimeframe.MonitorAlertTimeframe_4H.toString(),
    selected: false,
  });
  out.push({
    title: '6h',
    ID: EMonitorAlertTimeframe.MonitorAlertTimeframe_6H.toString(),
    selected: false,
  });
  out.push({
    title: '12h',
    ID: EMonitorAlertTimeframe.MonitorAlertTimeframe_12H.toString(),
    selected: false,
  });
  out.push({
    title: '24h',
    ID: EMonitorAlertTimeframe.MonitorAlertTimeframe_24H.toString(),
    selected: false,
  });

  out.forEach((_, i: number) => {
    if (out[i].ID === val.toString()) {
      out[i].selected = true;
    }
  });

  return out;
}

export function genTimeRange(
  key: EMonitorAlertTimeframe,
  hasNoTimeframe?: boolean,
): ISimpleOptionSelect[] {
  const out = [] as ISimpleOptionSelect[];

  if (hasNoTimeframe) {
    return out;
  }
  const selectedTimeframe = String(key);

  out.push({
    key: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_15MIN),
    value: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_15MIN),
    label: '15 MIN',
    isSelected: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_15MIN) === selectedTimeframe,
  });
  out.push({
    key: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_30MIN),
    value: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_30MIN),
    label: '30 MIN',
    isSelected: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_30MIN) === selectedTimeframe,
  });
  out.push({
    key: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_1H),
    value: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_1H),
    label: '1 HR',
    isSelected: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_1H) === selectedTimeframe,
  });
  out.push({
    key: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_2H),
    value: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_2H),
    label: '2 HR',
    isSelected: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_2H) === selectedTimeframe,
  });
  out.push({
    key: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_4H),
    value: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_4H),
    label: '4 HRS',
    isSelected: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_4H) === selectedTimeframe,
  });
  out.push({
    key: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_6H),
    value: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_6H),
    label: '6 HRS',
    isSelected: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_6H) === selectedTimeframe,
  });
  out.push({
    key: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_12H),
    value: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_12H),
    label: '12 HRS',
    isSelected: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_12H) === selectedTimeframe,
  });
  out.push({
    key: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_24H),
    value: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_24H),
    label: '24 HRS',
    isSelected: String(EMonitorAlertTimeframe.MonitorAlertTimeframe_24H) === selectedTimeframe,
  });
  return out;
}

export function monitorLookbackTypeToString(monitorLookbackType: EMonitorLookbackType): string {
  switch (monitorLookbackType) {
    case EMonitorLookbackType.MonitorLookbackType_NONE:
      return 'none';
    case EMonitorLookbackType.MonitorLookbackType_CONSECUTIVE:
      return 'consecutive';
    case EMonitorLookbackType.MonitorLookbackType_PREVIOUS_DAY:
      return 'previous day';
    case EMonitorLookbackType.MonitorLookbackType_PREVIOUS_WEEK:
      return 'previous week';
    default:
      return '';
  }
}

export function monitorThresholdComputationToString(
  thresholdComputation: EMonitorThresholdComputationType,
): string {
  switch (thresholdComputation) {
    case EMonitorThresholdComputationType.MonitorThresholdComputationType_SUM:
      return 'sum';
    case EMonitorThresholdComputationType.MonitorThresholdComputationType_AVG:
      return 'average';
    case EMonitorThresholdComputationType.MonitorThresholdComputationType_PERCENTAGE:
      return 'percentage';
    default:
      return '';
  }
}

export function monitorAlertTimeframeToString(
  monitorAlertTimeframe: EMonitorAlertTimeframe,
): string {
  switch (monitorAlertTimeframe) {
    case EMonitorAlertTimeframe.MonitorAlertTimeframe_15MIN:
      return '15m';
    case EMonitorAlertTimeframe.MonitorAlertTimeframe_30MIN:
      return '30m';
    case EMonitorAlertTimeframe.MonitorAlertTimeframe_1H:
      return '1h';
    case EMonitorAlertTimeframe.MonitorAlertTimeframe_2H:
      return '2h';
    case EMonitorAlertTimeframe.MonitorAlertTimeframe_4H:
      return '4h';
    case EMonitorAlertTimeframe.MonitorAlertTimeframe_6H:
      return '6h';
    case EMonitorAlertTimeframe.MonitorAlertTimeframe_12H:
      return '12h';
    case EMonitorAlertTimeframe.MonitorAlertTimeframe_24H:
      return '24h';
    case EMonitorAlertTimeframe.MonitorAlertTimeframe_Past48H_To_24H:
      return '48h-24h';
    case EMonitorAlertTimeframe.MonitorAlertTimeframe_NoTimeframe:
      return 'No timeframe';

    default:
      return '';
  }
}

export function genLookback(
  key: EMonitorLookbackType,
  isPercentage: boolean,
): ISimpleOptionSelect[] {
  const out = [] as ISimpleOptionSelect[];
  const lookbackType = String(key);
  if (!isPercentage) {
    out.push({
      key: String(EMonitorLookbackType.MonitorLookbackType_NONE),
      value: String(EMonitorLookbackType.MonitorLookbackType_NONE),
      label: 'No lookback',
      isSelected: String(EMonitorLookbackType.MonitorLookbackType_NONE) === lookbackType,
    });
  }
  out.push({
    key: String(EMonitorLookbackType.MonitorLookbackType_CONSECUTIVE),
    value: String(EMonitorLookbackType.MonitorLookbackType_CONSECUTIVE),
    label: 'Consecutive',
    isSelected: String(EMonitorLookbackType.MonitorLookbackType_CONSECUTIVE) === lookbackType,
  });
  out.push({
    key: String(EMonitorLookbackType.MonitorLookbackType_PREVIOUS_DAY),
    value: String(EMonitorLookbackType.MonitorLookbackType_PREVIOUS_DAY),
    label: 'Previous Day',
    isSelected: String(EMonitorLookbackType.MonitorLookbackType_PREVIOUS_DAY) === lookbackType,
  });
  out.push({
    key: String(EMonitorLookbackType.MonitorLookbackType_PREVIOUS_WEEK),
    value: String(EMonitorLookbackType.MonitorLookbackType_PREVIOUS_WEEK),
    label: 'Previous Week',
    isSelected: String(EMonitorLookbackType.MonitorLookbackType_PREVIOUS_WEEK) === lookbackType,
  });
  return out;
}

export function genMetricList(objectTypeID?: number): ELiveDataMetrics[] {
  const list = [];
  let metricsTypeIDs = new Set();

  // Filter Targeted metrics according to the selected Object type :

  if (objectTypeID === EMonitorObjectType.MonitorObjectType_CAMPAIGN) {
    // Filter Targeted metrics for Campaign Object Type:
    metricsTypeIDs = new Set([
      ELiveDataMetrics.CPM,
      ELiveDataMetrics.ADSERVER_REQUEST,
      ELiveDataMetrics.ADSERVER_FILLED,
      ELiveDataMetrics.ADSERVER_IMPRESSION,
      ELiveDataMetrics.ADSERVER_COMPLETE,
      ELiveDataMetrics.ADSERVER_QUARTILE1,
      ELiveDataMetrics.ADSERVER_QUARTILE2,
      ELiveDataMetrics.ADSERVER_QUARTILE3,
      ELiveDataMetrics.ADSERVER_INVENTORY_REQUEST,
      ELiveDataMetrics.METRICS_IDENTIFIER_REVENUE,
      ELiveDataMetrics.CAMPAIGN_PACING,
    ]);
  } else if (objectTypeID === EMonitorObjectType.MonitorObjectType_CHECKPOINT) {
    metricsTypeIDs = new Set([ELiveDataMetrics.CAMPAIGN_PACING]);
  } else if (
    objectTypeID === EMonitorObjectType.MonitorObjectType_SITE ||
    objectTypeID === EMonitorObjectType.MonitorObjectType_CHANNEL_GROUP
  ) {
    // Filter Targeted metrics for Channel Object Type:
    metricsTypeIDs = new Set([
      ELiveDataMetrics.ENDPOINT_REQUEST,
      ELiveDataMetrics.ENDPOINT_LATENCY,
      ELiveDataMetrics.ENDPOINT_RESPONSE,
      ELiveDataMetrics.BID_WON,
      ELiveDataMetrics.IMPRESSIONS,
      ELiveDataMetrics.CPM,
      ELiveDataMetrics.AUCTION_INIT,
      ELiveDataMetrics.AUCTION_LATENCY,
      ELiveDataMetrics.AUCTION_END,
      ELiveDataMetrics.BIDDER_LATENCY,
      ELiveDataMetrics.BID_RESPONSE,
      ELiveDataMetrics.BID_REQUEST,
      ELiveDataMetrics.BID_ERROR,
      ELiveDataMetrics.BID_TIMEOUT,
      ELiveDataMetrics.METRICS_IDENTIFIER_BID_ERROR_RATE,
      ELiveDataMetrics.METRICS_IDENTIFIER_REVENUE,
      ELiveDataMetrics.METRICS_IDENTIFIER_FILL_RATE,
      ELiveDataMetrics.METRICS_IDENTIFIER_CUSTOM_METRIC,
      ELiveDataMetrics.METRICS_IDENTIFIER_PUBLICA_VAULT_LIFT,
    ]);
  } else if (
    objectTypeID === EMonitorObjectType.MonitorObjectType_BIDDER ||
    objectTypeID === EMonitorObjectType.MonitorObjectType_DEMAND_SOURCE ||
    objectTypeID === EMonitorObjectType.MonitorObjectType_BIDDER_GROUP
  ) {
    // Filter Targeted metrics for Demand Source, Bidder and Bidder Group Object Types:
    metricsTypeIDs = new Set([
      ELiveDataMetrics.IMPRESSIONS,
      ELiveDataMetrics.BIDDER_LATENCY,
      ELiveDataMetrics.BID_RESPONSE,
      ELiveDataMetrics.BID_WON,
      ELiveDataMetrics.BID_REQUEST,
      ELiveDataMetrics.BID_ERROR,
      ELiveDataMetrics.BID_TIMEOUT,
      ELiveDataMetrics.METRICS_IDENTIFIER_BID_ERROR_RATE,
      ELiveDataMetrics.METRICS_IDENTIFIER_REVENUE,
      ELiveDataMetrics.CPM,
      ELiveDataMetrics.METRICS_IDENTIFIER_PUBLICA_VAULT_LIFT,
    ]);
  } else if (objectTypeID === EMonitorObjectType.MonitorObjectType_PUBLISHER) {
    // Filter Targeted metrics for Publisher Object Type:
    metricsTypeIDs = new Set([
      ELiveDataMetrics.ENDPOINT_REQUEST,
      ELiveDataMetrics.IMPRESSIONS,
      ELiveDataMetrics.BID_WON,
      ELiveDataMetrics.AUCTION_INIT,
      ELiveDataMetrics.CPM,
      ELiveDataMetrics.ENDPOINT_LATENCY,
      ELiveDataMetrics.AUCTION_LATENCY,
      ELiveDataMetrics.BIDDER_LATENCY,
      ELiveDataMetrics.BID_RESPONSE,
      ELiveDataMetrics.ENDPOINT_RESPONSE,
      ELiveDataMetrics.AUCTION_END,
      ELiveDataMetrics.BID_REQUEST,
      ELiveDataMetrics.BID_ERROR,
      ELiveDataMetrics.BID_TIMEOUT,
      ELiveDataMetrics.ADSERVER_REQUEST,
      ELiveDataMetrics.ADSERVER_FILLED,
      ELiveDataMetrics.ADSERVER_IMPRESSION,
      ELiveDataMetrics.ADSERVER_COMPLETE,
      ELiveDataMetrics.ADSERVER_QUARTILE1,
      ELiveDataMetrics.ADSERVER_QUARTILE2,
      ELiveDataMetrics.ADSERVER_QUARTILE3,
      ELiveDataMetrics.ADSERVER_INVENTORY_REQUEST,
      ELiveDataMetrics.METRICS_IDENTIFIER_REVENUE,
      ELiveDataMetrics.METRICS_IDENTIFIER_BID_ERROR_RATE,
      ELiveDataMetrics.METRICS_IDENTIFIER_FILL_RATE,
      ELiveDataMetrics.METRICS_IDENTIFIER_CUSTOM_METRIC,
      ELiveDataMetrics.CAMPAIGN_PACING,
      ELiveDataMetrics.METRICS_IDENTIFIER_PUBLICA_VAULT_LIFT,
    ]);
  } else if (objectTypeID === EMonitorObjectType.MonitorObjectType_NETWORK) {
    // Filter Targeted metrics for Publisher Object Type:
    metricsTypeIDs = new Set([
      ELiveDataMetrics.STREAM_AD_FILLED,
      ELiveDataMetrics.STREAM_POD_REQUEST,
      ELiveDataMetrics.STREAM_ERROR,
      ELiveDataMetrics.STREAM_POD_FILLED,
      ELiveDataMetrics.STREAM_AD_IMPRESSION,
      ELiveDataMetrics.STREAM_AD_FILLED_LATENCY,
      ELiveDataMetrics.STREAM_AD_FETCH_TIMEOUT,
      ELiveDataMetrics.STREAM_CHUNKLIST_FETCH,
      ELiveDataMetrics.STREAM_CHUNKLIST_RESPONSE,
      ELiveDataMetrics.STREAM_START,
      ELiveDataMetrics.STREAM_CHUNKLIST_TIMEOUT,
      ELiveDataMetrics.STREAM_PLAYLIST_TIMEOUT,
    ]);
  }

  for (const eLiveDataMetricsKey in ELiveDataMetrics) {
    if (isNaN(Number(eLiveDataMetricsKey))) {
      continue;
    }
    if (metricsTypeIDs.has(Number(eLiveDataMetricsKey)) || !objectTypeID) {
      const value = Number(eLiveDataMetricsKey) as ELiveDataMetrics;
      const key = ELiveDataMetricsKey.get(value) || '';

      const metricString = ELiveDataMetricsString(key as any);
      if (metricString !== undefined) {
        list.push({
          metric: value,
          metricString: ELiveDataMetricsString(key as any),
        });
      }
    }
  }

  // sort
  const sortedList = list.sort((a, b) => {
    return a.metricString.localeCompare(b.metricString);
  });

  return sortedList.map((value) => value.metric);
}

export function MonitorTriggerTypeToString(m: EMonitorTriggerType): string {
  switch (m) {
    case EMonitorTriggerType.MonitorTriggerType_ALERT:
      return 'Alert';
    case EMonitorTriggerType.MonitorTriggerType_WARNING:
      return 'Warning';
    case EMonitorTriggerType.MonitorTriggerType_ALERT_RECOVERY:
      return 'Alert Recovery';
    case EMonitorTriggerType.MonitorTriggerType_WARNING_RECOVERY:
      return 'Warning Recovery';
    case EMonitorTriggerType.MonitorTriggerType_SHUTDOWN:
      return 'Shutdown';
  }
  return 'unknown';
}

// if we select 2h in period, we will filter out every values > 2h
export function checkThatPeriodAIsWithinPeriodB(
  periodA: EMonitorAlertTimeframe,
  periodB: EMonitorAlertTimeframe,
): boolean {
  return monitorAlertTimeFrameOrdered(periodA) <= monitorAlertTimeFrameOrdered(periodB);
}

export function NumberWithCommas(x: number, maxDigits: number = 2): string {
  return x.toFixed(maxDigits).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function formatNumberWithCommasAndSign(
  x: number,
  isPercentage: boolean,
  maxDigits: number = 2,
): string {
  const sign = x > 0 ? '+' : x < 0 ? '-' : '+';
  const absX = Math.abs(x);
  const formattedNumber = absX.toFixed(maxDigits).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return sign + formattedNumber + (isPercentage ? '%' : '');
}

export function genTriggerTypeList(
  removeWarning: boolean,
): Array<{ ID: EMonitorTriggerType; name: string }> {
  const out = [] as Array<{ ID: EMonitorTriggerType; name: string }>;

  out.push({
    ID: EMonitorTriggerType.MonitorTriggerType_ALERT,
    name: 'Alert',
  });
  if (!removeWarning) {
    out.push({
      ID: EMonitorTriggerType.MonitorTriggerType_WARNING,
      name: 'Warning',
    });
  }
  out.push({
    ID: EMonitorTriggerType.MonitorTriggerType_ALERT_RECOVERY,
    name: 'Alert Recovery',
  });

  if (!removeWarning) {
    out.push({
      ID: EMonitorTriggerType.MonitorTriggerType_WARNING_RECOVERY,
      name: 'Warning Recovery',
    });
  }

  return out;
}

export function triggerTypeToString(triggerType: EMonitorTriggerType): string {
  switch (triggerType) {
    case EMonitorTriggerType.MonitorTriggerType_ALERT:
      return 'Alert';
    case EMonitorTriggerType.MonitorTriggerType_WARNING:
      return 'Warning';
    case EMonitorTriggerType.MonitorTriggerType_ALERT_RECOVERY:
      return 'Alert Recovery';
    case EMonitorTriggerType.MonitorTriggerType_WARNING_RECOVERY:
      return 'Warning Recovery';
    case EMonitorTriggerType.MonitorTriggerType_SHUTDOWN:
      return 'Shutdown';
  }
  return 'unknown trigger type';
}

export function providerToString(provider: EMonitorNotificationProvider): string {
  switch (provider) {
    case EMonitorNotificationProvider.MonitorNotificationProvider_URL:
      return 'Url';
    case EMonitorNotificationProvider.MonitorNotificationProvider_EMAIL:
      return 'Email';
    case EMonitorNotificationProvider.MonitorNotificationProvider_SLACK:
      return 'Slack';
    case EMonitorNotificationProvider.MonitorNotificationProvider_OPSGENIE:
      return 'Opsgenie';
  }
  return 'unknown provider type';
}

let cnter = 0;
const alertTimeFrameOrdered = new Map<EMonitorAlertTimeframe, number>([
  [EMonitorAlertTimeframe.MonitorAlertTimeframe_15MIN, cnter++],
  [EMonitorAlertTimeframe.MonitorAlertTimeframe_30MIN, cnter++],
  [EMonitorAlertTimeframe.MonitorAlertTimeframe_1H, cnter++],
  [EMonitorAlertTimeframe.MonitorAlertTimeframe_2H, cnter++],
  [EMonitorAlertTimeframe.MonitorAlertTimeframe_4H, cnter++],
  [EMonitorAlertTimeframe.MonitorAlertTimeframe_6H, cnter++],
  [EMonitorAlertTimeframe.MonitorAlertTimeframe_12H, cnter++],
  [EMonitorAlertTimeframe.MonitorAlertTimeframe_24H, cnter++],
]);

export function monitorAlertTimeFrameOrdered(timeframe: EMonitorAlertTimeframe): number {
  const idx = alertTimeFrameOrdered.get(timeframe);
  if (idx === undefined) {
    console.warn("monitorAlertTimeFrameOrdered: can't find ", timeframe, ' in map');
    return -1;
  }
  return idx;
}

// for Targeted Metric Pill Switcher
export type TargetedMetricCondition = number;
export const TargetedMetricConditionUnknown: TargetedMetricCondition = 0;
export const TargetedMetricConditionSingle: TargetedMetricCondition = 1;
export const TargetedMetricConditionCustom: TargetedMetricCondition = 2;

export function monitorTargetedMetricConditionTypeToString(source_type: TargetedMetricCondition) {
  switch (source_type) {
    case TargetedMetricConditionSingle:
      return 'Defined Metric';
    case TargetedMetricConditionCustom:
      return 'Custom';
    default:
      break;
  }
  return 'unknown';
}

export function generateMonitorMetricMenuList(
  skipRates?: boolean,
  selectedMetricID?: number,
  disableID?: number,
  objectTypeID?: number,
): IMenuList {
  const monitorMetricsList = genMetricList(objectTypeID);
  const monitorMetricsMenuList = {} as IMenuList;

  for (const metricID of monitorMetricsList) {
    // skip unknown (0), metrics that are already rates, and custom metric enum
    if (
      metricID === 0 ||
      (skipRates && !!ELiveDataTargetedRateMetrics[metricID]) ||
      metricID === ELiveDataMetrics.METRICS_IDENTIFIER_CUSTOM_METRIC
    ) {
      continue;
    }
    monitorMetricsMenuList[metricID] = {
      ID: Number(metricID).toString(),
      selected: Number(metricID) === selectedMetricID,
      title: ELiveDataMetricsString(ELiveDataMetricsKey.get(metricID) as any),
      disabled: metricID === disableID,
    } as IListItem;
  }
  return monitorMetricsMenuList;
}

// generateMonitorCustomMetricList creates a default menu list from the provided country groups
export function generateMonitorCustomMetricList(
  monitorCustomMetrics: IMonitorCustomMetric[],
  selectedCustomMetricID?: number,
): IMenuList {
  const monitorCustomMetricList: IMenuList = {};

  for (const monitorCustomMetric of monitorCustomMetrics) {
    monitorCustomMetricList[monitorCustomMetric.ID] = {
      ID: monitorCustomMetric.ID.toString(),
      selected: monitorCustomMetric.ID === selectedCustomMetricID,
      title: monitorCustomMetric.Name,
    } as IListItem;
  }
  return monitorCustomMetricList;
}

export function getSelectedMonitorCustomMetricFromList(
  monitorMetricList: IMenuList,
  monitorCustomMetrics: IMonitorCustomMetric[],
): IMonitorCustomMetric {
  let selectedCustomMetric = {} as IMonitorCustomMetric;
  monitorCustomMetrics.forEach((metric) => {
    if (monitorMetricList[metric.ID].selected) {
      selectedCustomMetric = metric;
    }
  });
  return selectedCustomMetric;
}

export function getSelectedMetricIDFromMenuList(monitorMetricList: IMenuList): ELiveDataMetrics {
  let selectedMetricID = 0 as ELiveDataMetrics;
  for (const monitorMetric of Object.values(monitorMetricList)) {
    if (monitorMetric.selected) {
      selectedMetricID = parseInt(monitorMetric.ID, 10);
      return selectedMetricID;
    }
  }
  return selectedMetricID;
}

// adds an array of ids and names to each alert for display in <MonitorHistory>
export function enrichMonitorAlertsWithTargetedObjectIDsAndNames(
  monitor: IMonitor,
  list: IMenuList,
): IMonitor {
  if (monitor.AlertHistory === null) {
    monitor.AlertHistory = [];
  }
  for (let i = 0; i < monitor.AlertHistory.length; i++) {
    // add IListItems from the list to the alert history for every id in the alert
    const targetedObjectIDs = monitor.AlertHistory[i].TargetedObject.split(',');
    monitor.AlertHistory[i].listItems = [] as IListItem[];
    for (const id of targetedObjectIDs) {
      if (list[id]) {
        monitor.AlertHistory[i].listItems.push(list[id]);
      }
    }
  }

  return monitor;
}
